import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ImageUploaderWithPreview from "@entrilia/shared/components/imageUploader/ImageUploaderWithPreview";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ImagePlaceholderIcon from "@entrilia/shared/icons/ImagePlaceholderIcon";
import adminApi from "../../api/adminApi";
import type { ObjectFieldValues, ObjectFieldValuesDetails } from "../../api/types/objectTypes";
import EntitySection from "./EntitySection";
import EntitySectionHeader from "./EntitySectionHeader";

interface Props {
  entity: ObjectFieldValues;
  disabled?: boolean;
  onEntityUpdate: (newObjectData: ObjectFieldValuesDetails) => void;
}

const logoImageTypes = ["image/png", "image/jpeg"];
const updateObjectLogo = withErrorHandling(adminApi.updateObjectLogo);
const removeObjectLogo = withErrorHandling(adminApi.removeObjectLogo);

const ObjectLogoSection = ({ entity, disabled, onEntityUpdate }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const [logoUrl, setLogoUrl] = useState(entity.logoUrl);
  const [isSavingLogo, setIsSavingLogo] = useState(false);

  const handleImageSelected = async (file: File | undefined) => {
    if (file === undefined) {
      return;
    }

    setIsSavingLogo(true);
    const [resp, error] = await updateObjectLogo(entity.objectType, entity.id, file);
    setIsSavingLogo(false);

    if (error) {
      sendNotificationError(error.message);
    } else {
      setLogoUrl(resp.logoUrl);
      onEntityUpdate(resp);
    }
  };

  const handleDeleteClick = async () => {
    setIsSavingLogo(true);
    const [resp, error] = await removeObjectLogo(entity.objectType, entity.id);
    setIsSavingLogo(false);

    if (error) {
      sendNotificationError(error.message);
    } else {
      setLogoUrl(resp.logoUrl);
      onEntityUpdate(resp);
    }
  };

  return (
    <EntitySection>
      <EntitySectionHeader title="Company Logo" />
      <Stack spacing={1.5} sx={{ width: "100%", px: 2.5, pb: 2 }}>
        <Typography color="textSecondary">PNG, JPG. Recommended size: 140x56 pixels.</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <ImageUploaderWithPreview
            disabled={disabled || isSavingLogo}
            width={140}
            height={56}
            allowedImageTypes={logoImageTypes}
            imageSrc={logoUrl}
            onImageSelected={handleImageSelected}
            uploadButtonName="Upload Logomark"
            showUploadButtonIcon
            maxFileSizeBytes={500_000}
            buttonsPosition="right"
            border="dashed"
            Placeholder={
              <Stack sx={{ p: 2, alignItems: "center" }}>
                <ImagePlaceholderIcon />
              </Stack>
            }
          />
          <Button
            disabled={disabled || isSavingLogo}
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        </Stack>
      </Stack>
    </EntitySection>
  );
};

export default ObjectLogoSection;
